import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isMobileDevice = () => {
  // 서버 환경에서는 window 객체가 없으므로 false 반환
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent;

  // 모바일 기기 User-Agent 체크
  const mobileRegex =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  return mobileRegex.test(userAgent);
};

/**
 * 언더바(_)를 사용하는 JSON 데이터를 카멜 케이스로 변환합니다.
 * @param snakeCaseObj 언더바(_) 형식의 객체
 * @returns 카멜 케이스로 변환된 객체
 */
export function convertToCamelCase(
  snakeCaseObj: Record<string, unknown>,
): Record<string, unknown> {
  const toCamel = (str: string) =>
    str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

  const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

  const camelCaseObj: Record<string, unknown> = {};
  Object.keys(snakeCaseObj).forEach((key) => {
    const camelKey = toCamel(key);
    const value = snakeCaseObj[key];

    if (isObject(value)) {
      camelCaseObj[camelKey] = convertToCamelCase(value);
    } else if (Array.isArray(value)) {
      camelCaseObj[camelKey] = value.map((item) =>
        isObject(item) ? convertToCamelCase(item) : item,
      );
    } else {
      camelCaseObj[camelKey] = value;
    }
  });

  return camelCaseObj;
}

/**
 * 응답 데이터를 처리합니다.
 * @param response success:json:으로 시작하는 응답 데이터
 * @returns JSON 객체 또는 배열
 */
export function parseResponse<T extends object | unknown[]>(
  response: string,
): T {
  try {
    // `success:json:` 접두어 제거
    const jsonString = response.replace(/^success:json:/, '');
    const parsedData = JSON.parse(jsonString) as unknown;

    // 배열과 객체를 카멜 케이스로 변환하는 함수
    const convertToCamelCaseDeep = (data: unknown): unknown => {
      if (Array.isArray(data)) {
        // 배열인 경우 각 요소를 재귀적으로 처리하여 카멜 케이스로 변환
        return data.map((item) => convertToCamelCaseDeep(item));
      }
      if (typeof data === 'object' && data !== null) {
        // 객체인 경우 키를 카멜 케이스로 변환하고 값을 재귀적으로 처리
        const camelCasedObject = convertToCamelCase(
          data as Record<string, unknown>,
        );
        return Object.entries(camelCasedObject).reduce<Record<string, unknown>>(
          (acc, [key, value]) => {
            acc[key] = convertToCamelCaseDeep(value);
            return acc;
          },
          {},
        );
      }
      // 기본 값은 그대로 반환
      return data;
    };

    // 최종적으로 데이터 전체에 대해 카멜 케이스 변환
    const normalizedData = convertToCamelCaseDeep(parsedData);

    if (Array.isArray(normalizedData)) {
      return normalizedData as T;
    }

    if (typeof normalizedData === 'object' && normalizedData !== null) {
      return normalizedData as T;
    }

    console.error('Invalid JSON response format');
    return [] as unknown as T; // 빈 배열 반환
  } catch (error) {
    console.error('Error parsing response:', error);
    return [] as unknown as T; // 기본적으로 빈 배열 반환
  }
}

export function maskName(name: string | undefined): string {
  if (!name) {
    return '';
  }
  if (name.length <= 2) {
    return name[0] + '*';
  }

  const middleIndex = Math.floor(name.length / 2);

  if (name.length % 2 === 0) {
    return name.slice(0, middleIndex - 1) + '**' + name.slice(middleIndex + 1);
  } else {
    return name.slice(0, middleIndex) + '*' + name.slice(middleIndex + 1);
  }
}

export const truncateName = (name: string, maxLength = 3) => {
  return name.length > maxLength ? name.slice(0, maxLength) + '...' : name;
};

// ✅ 날짜 포멧팅 `YYYY-MM-DD` 변환
export const formatToKoreaDate = (date: string | number | Date) => {
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(new Date(date))
    .replace(/(\. )/g, '-')
    .replace(/\./g, '')
    .trim();
};
// ✅ 날짜 포멧팅 `YYYY-MM-DD HH:MM:SS` 변환
export const formatToKoreaDateTime = (date: string | number | Date) => {
  const d = new Date(date);

  // ✅ 날짜 포맷 (YYYY-MM-DD)
  const dateString = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(d)
    .replace(/(\. )/g, '-')
    .replace(/\./g, '')
    .trim();

  // ✅ 시간 포맷 (HH:mm)
  const timeString = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // 24시간제
  })
    .format(d)
    .replace(/\s/g, '') // 공백 제거
    .trim();

  return `${dateString} ${timeString}`; // ✅ "YYYY-MM-DD HH:mm" 형식
};

/**
 * formatToKoreaDateTime 결과에서 구분자를 점(.)으로 바꾼 형식 (yyyy.MM.dd HH:mm)
 */
export const formatToKoreaDateTimeWithDots = (
  date: string | number | Date,
): string => {
  return formatToKoreaDateTime(date).replace(/-/g, '.');
};

/**
 * 날짜만 점(.) 구분, 월/일 앞자리 0 없음 (yyyy.m.d). 예: 2026.1.20
 */
export const formatToKoreaDateShort = (
  date: string | number | Date,
): string => {
  const d = new Date(date);
  return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`;
};

export const formatToKoreanMinutesSeconds = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  // const paddedMins = String(mins).padStart(2, '0');
  // const paddedSecs = String(secs).padStart(2, '0');

  return `${mins}분 ${secs}초`;
};

// ✅ 한국 시간 기준 `YYYY-MM-DD` 변환
export const getKoreaDateString = (): string => {
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .format(new Date()) // 현재 날짜를 한국 시간 기준으로 변환
    .replace(/(\. )/g, '-') // "YYYY. MM. DD" → "YYYY-MM-DD"
    .replace(/\./g, '') // 혹시라도 남은 "." 제거
    .trim(); // 공백 제거
};

export const formatDateTime = (date: Date): string => {
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
};
export const apiFormatDateTime = (date: Date): string => {
  const pad = (num: number) => num.toString().padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // getMonth()는 0부터 시작하므로 +1 필요
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// ✅ 현재 한국 날짜 및 시간 가져오기
export const getCurrentKoreaDate = () => formatToKoreaDate(new Date());
export const getCurrentKoreaDateTime = () => formatToKoreaDateTime(new Date());

// 광고 시청 시간 계산 (초 단위)
export const calculateAdPlaytime = (start: string, end: string): number => {
  return Math.floor(
    (new Date(end).getTime() - new Date(start).getTime()) / 1000,
  );
};
export function formatDateToYYYYMMDD(dateInput: string | Date): string {
  const date = new Date(dateInput);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

// ✅ HTML 태그 제거
export const stripHtmlTags = (htmlString: string): string => {
  return htmlString.replace(/<[^>]*>/g, '');
};

export function isNotEmpty(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

/**
 * 클립보드에 텍스트를 복사합니다.
 * navigator.clipboard는 HTTPS/localhost에서만 사용 가능하므로,
 * 없을 경우 execCommand('copy')로 폴백합니다.
 * @returns 복사 성공 여부
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 권한 거부 등으로 실패 시 폴백 시도
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
